import React, { useMemo } from "react";
import { backend, UmlsEntityType, validateAddRule, WitsConcept } from "Backend";
import { Box, Button, Grid, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import Flex from "../../components/Flex";
import { P } from "theme/typography";
import { nonEmpty } from "../../helpers/coll";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addNewRule,
  browseConcepts,
  selectAllEntityIds,
  selectCurrentField,
  selectDraftMode,
  selectLensEditorDraftRules,
  selectLensEditorLens,
  selectSelectedRuleTypeComponents,
} from "../../redux/slices/lensEditor";
import { EntityType } from "../../generated/graphql";
import { Warning } from "@mui/icons-material";
import { RuleMode } from "routes/lens-editor/RuleMode";
import CopyToClipboard from "../../components/CopyToClipboard";

export interface WitsConceptCardProps {
  concept: WitsConcept;
}

function umlsToEntityType(umls: UmlsEntityType): EntityType | undefined {
  switch (umls) {
    case "condition":
      return EntityType.Condition;
    case "medication":
      return EntityType.Medication;
    case "procedure":
      return EntityType.Procedure;
    case "other":
      return EntityType.Other;
    default:
      return undefined;
  }
}

const WitsConceptInfo = (props: WitsConceptCardProps) => {
  const { concept } = props;
  const dispatch = useAppDispatch();
  const lens = useAppSelector(selectLensEditorLens);
  const lensId = lens.id;
  const selectedField = useAppSelector(selectCurrentField);
  const draftMode = useAppSelector(selectDraftMode);
  const [children, setChildren] = React.useState<WitsConcept[]>([]);
  const entityIds = useAppSelector(selectAllEntityIds);
  const rules = useAppSelector(selectLensEditorDraftRules);
  const { subtypes, descriptions } = useAppSelector(selectSelectedRuleTypeComponents);
  const isInLens = entityIds.has(concept.id);

  const duplicateRules = useMemo(() => {
    return rules.filter((r) => r.entityQuery?.entityId === concept.id);
  }, [rules, concept.id]);

  const loadChildren = async () => {
    const result = await backend.conceptChildren([concept.id]);
    const children = result.concepts[0];

    if (children !== undefined) {
      setChildren(children);
    }
  };

  const addToLens = (concept: WitsConcept) => {
    const entityType = umlsToEntityType(concept.umlsEntityType);
    if (entityType) {
      dispatch(
        addNewRule({
          lensId,
          fieldId: selectedField?.id,
          entityQuery: { entityId: concept.id, entityType: entityType },
        })
      );
    }
  };

  const addRuleValid = validateAddRule({
    draftMode,
    field: selectedField,
    subtypes,
    descriptions,
  });

  return (
    <Flex maxWidth="600px" maxHeight="400px">
      <div>
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <P variant="h6" sx={{ mr: 1 }}>
            {concept.canonicalName}
          </P>
          <P>{concept.id}</P>
          <CopyToClipboard sx={{ color: "grey.400" }} text={concept.id} />
        </Box>
      </div>
      <List sx={{ maxHeight: "300px", overflow: "auto" }}>
        <ListItem divider>
          <ListItemText>Entity Type</ListItemText>
          <ListItemText>{concept.umlsEntityType}</ListItemText>
        </ListItem>
        {concept.penalty !== 0 && (
          <ListItem divider>
            <ListItemText>Penalty</ListItemText>
            <ListItemText>{concept.penalty}</ListItemText>
          </ListItem>
        )}
        {nonEmpty(concept.codes) && (
          <ListItem divider>
            <ListItemText>Codes</ListItemText>
            <Grid sx={{ maxHeight: "200px", overflow: "auto" }}>
              {concept.codes.map((code, idx) => (
                <P sx={{ color: "grey.400" }} key={idx}>
                  {code.source}: {code.code}
                </P>
              ))}
            </Grid>
          </ListItem>
        )}
        {nonEmpty(concept.requiredFragments) && (
          <ListItem divider>
            <ListItemText>Required Words</ListItemText>
            <Flex flexDirection="row" flexWrap="wrap">
              {concept.requiredFragments.map((reqGroup, idx) =>
                reqGroup.split("|").map((word) => (
                  <P sx={{ fontSize: "0.6rem", mr: "4px", color: "grey.400" }} key={idx.toString() + word}>
                    &quot;{word}&quot;
                  </P>
                ))
              )}
            </Flex>
          </ListItem>
        )}
        <ListItem divider>
          <ListItemText>Aliases</ListItemText>
          <Flex flexDirection="row" flexWrap="wrap" sx={{ maxHeight: "200px", overflow: "auto" }}>
            {concept.aliases.map((alias, idx) => (
              <P sx={{ fontSize: "0.6rem", mr: "4px", color: "grey.400" }} key={idx}>
                &quot;{alias}&quot;
              </P>
            ))}
          </Flex>
        </ListItem>
        {concept.refinedType && (
          <ListItem divider>
            <ListItemText>Type</ListItemText>
            <ListItemText>{concept.refinedType}</ListItemText>
          </ListItem>
        )}
      </List>
      {nonEmpty(children) ? (
        <Flex p={1} marginTop={2} overflow="auto" maxHeight="200px">
          <Button onClick={() => dispatch(browseConcepts({ concepts: children }))}>Browse Children</Button>
          {children.map((c, idx) => (
            <P sx={{ fontWeight: "normal", color: "grey.400" }} key={c.id}>
              {idx + 1} {c.canonicalName}
            </P>
          ))}
        </Flex>
      ) : (
        <Button onClick={loadChildren}>Show children</Button>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: 1,
        }}>
        <Tooltip title={addRuleValid.message}>
          <span>
            <Button
              disabled={!addRuleValid.allowAdd}
              onClick={() => addToLens(concept)}
              startIcon={isInLens ? <Warning /> : undefined}>
              {isInLens ? `Add to Lens (Duplicate)` : "Add to Lens"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <List sx={{ maxHeight: "200px", overflow: "auto" }}>
        {duplicateRules.map((rule) => (
          <ListItem key={rule.ruleId} divider>
            <RuleMode rule={rule} hideConceptButton />
          </ListItem>
        ))}
      </List>
    </Flex>
  );
};

export default WitsConceptInfo;
